import { API_CONFIG } from 'config/api';

const TRANSACTION_BASE_URL = API_CONFIG.TRANSACTION.BASE_URL;

export interface TransactionFilters {
  page?: number;
  limit?: number;
  type?: 'CREDIT' | 'DEBIT';
  status?: 'PENDING' | 'SUCCESS' | 'FAILED';
  category?: 'TRANSFER' | 'BILL' | 'FUNDING' | 'WITHDRAWAL' | 'DEPOSIT' | 'REFUND' | 'FEE' | 'LOAN';
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface Transaction {
  id: string;
  customerId: string;
  type: string;
  amount: number;
  description: string;
  status: string;
  reference: string;
  category: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionStatistics {
  totalTransactions: number;
  totalAmount: number;
  creditAmount: number;
  debitAmount: number;
  pendingAmount: number;
  completedAmount: number;
  failedAmount: number;
  averageTransactionAmount: number;
  transactionCountByType: Record<string, number>;
  transactionCountByStatus: Record<string, number>;
  monthlyTrend: Array<{
    month: string;
    count: number;
    amount: number;
  }>;
}

export interface TransactionResponse {
  success: boolean;
  data?: {
    transactions: Transaction[];
    pagination: {
      currentPage: number;
      totalPages: number;
      total: number;
      pageSize: number;
    };
  };
  message?: string;
}

export interface TransactionStatsResponse {
  success: boolean;
  data?: TransactionStatistics;
  message?: string;
}

export class TransactionApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`${TRANSACTION_BASE_URL}${API_CONFIG.TRANSACTION.ENDPOINTS.TRANSACTIONS}?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get transactions API error:', error);
      throw new Error('Failed to fetch transactions');
    }
  }

  async getTransactionById(transactionId: string): Promise<{ success: boolean; data?: Transaction; message?: string }> {
    try {
      const response = await fetch(`${TRANSACTION_BASE_URL}${API_CONFIG.TRANSACTION.ENDPOINTS.TRANSACTIONS}/${transactionId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get transaction by ID API error:', error);
      throw new Error('Failed to fetch transaction');
    }
  }

  async getTransactionStatistics(period: string = 'monthly'): Promise<TransactionStatsResponse> {
    try {
      const response = await fetch(`${TRANSACTION_BASE_URL}${API_CONFIG.TRANSACTION.ENDPOINTS.STATISTICS}?period=${period}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get transaction statistics API error:', error);
      throw new Error('Failed to fetch transaction statistics');
    }
  }

  async getTransactionsByEmployee(employeeId: string, filters: Omit<TransactionFilters, 'employeeId'> = {}): Promise<TransactionResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('employeeId', employeeId);
      
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`${TRANSACTION_BASE_URL}${API_CONFIG.TRANSACTION.ENDPOINTS.BY_EMPLOYEE}?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get transactions by employee API error:', error);
      throw new Error('Failed to fetch employee transactions');
    }
  }

  // Alias method for hooks compatibility
  async getEmployeeTransactions(employeeId: string, filters: TransactionFilters = {}): Promise<TransactionResponse> {
    return this.getTransactionsByEmployee(employeeId, filters);
  }
}

export const transactionApiService = new TransactionApiService();
